import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiCall } from '../api/client';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

const DOE_TYPES = [
  { id: 'handyman', label: 'General Doe', icon: '🔨' },
  { id: 'plumbing', label: 'Plumber Doe', icon: '🔧' },
  { id: 'electrical', label: 'Electrician Doe', icon: '⚡' },
  { id: 'hvac', label: 'HVAC Doe', icon: '❄️' },
  { id: 'landscaping', label: 'Landscaper Doe', icon: '🌿' },
  { id: 'cleaning', label: 'Cleaner Doe', icon: '✨' },
];

const PROBLEM_TAGS: Record<string, { id: string; label: string }[]> = {
  plumbing: [
    { id: 'plumb-leak', label: 'Leak' },
    { id: 'plumb-clog', label: 'Clogged' },
    { id: 'plumb-nohotwater', label: 'No Hot Water' },
    { id: 'plumb-other', label: 'Other' },
  ],
  electrical: [
    { id: 'elec-nopower', label: 'No Power' },
    { id: 'elec-outlet', label: 'Outlet Issue' },
    { id: 'elec-wiring', label: 'Wiring' },
    { id: 'elec-other', label: 'Other' },
  ],
  hvac: [
    { id: 'hvac-noheat', label: 'No Heat' },
    { id: 'hvac-noac', label: 'No AC' },
    { id: 'hvac-thermostat', label: 'Thermostat' },
    { id: 'hvac-other', label: 'Other' },
  ],
  handyman: [
    { id: 'handy-drywall', label: 'Drywall' },
    { id: 'handy-door', label: 'Door/Window' },
    { id: 'handy-paint', label: 'Painting' },
    { id: 'handy-other', label: 'Other' },
  ],
  landscaping: [
    { id: 'land-mow', label: 'Mowing' },
    { id: 'land-tree', label: 'Tree Trim' },
    { id: 'land-cleanup', label: 'Cleanup' },
    { id: 'land-other', label: 'Other' },
  ],
  cleaning: [
    { id: 'clean-deep', label: 'Deep Clean' },
    { id: 'clean-movein', label: 'Move-In/Out' },
    { id: 'clean-regular', label: 'Regular' },
    { id: 'clean-other', label: 'Other' },
  ],
};

const URGENCY_OPTIONS = [
  { id: 'now', label: 'Now', color: '#DC2626' },
  { id: 'today', label: 'Today', color: '#F59E0B' },
  { id: 'this_week', label: 'This Week', color: '#10B981' },
];

const BUDGET_OPTIONS = [
  { id: 'under_100', label: 'Under $100' },
  { id: '100_to_500', label: '$100–$500' },
  { id: 'over_500', label: '$500+' },
  { id: 'not_sure', label: 'Not Sure' },
];

type Step = 'doe_type' | 'problem' | 'urgency_budget' | 'details' | 'confirm';

export default function SendADoe() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState<Step>('doe_type');
  const [doeType, setDoeType] = useState('');
  const [problemTag, setProblemTag] = useState('');
  const [urgency, setUrgency] = useState('');
  const [budget, setBudget] = useState('');
  const [description, setDescription] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!zipCode) {
      toast.error('Please enter your zip code');
      return;
    }
    setSubmitting(true);
    try {
      const result = await apiCall<{ id: string; matched_contractors: number }>('/requests', {
        method: 'POST',
        body: JSON.stringify({
          trade_category_id: doeType,
          problem_tag_id: problemTag || undefined,
          description: description || undefined,
          contact_phone: contactPhone || undefined,
          zip_code: zipCode,
          urgency,
          budget_range: budget,
        }),
      });

      if (result.success && result.data) {
        confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
        toast.success(`Doe dispatched! ${result.data.matched_contractors} contractors notified.`);
        navigate(`/requests/${result.data.id}`);
      } else {
        toast.error(result.error || 'Failed to send request');
      }
    } catch (err) {
      toast.error('Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const stepLabels: Record<Step, string> = {
    doe_type: 'Pick your Doe',
    problem: 'What\'s the problem?',
    urgency_budget: 'How urgent? What\'s the budget?',
    details: 'Any extra details?',
    confirm: 'Confirm & Send',
  };

  return (
    <div className="page" style={{ maxWidth: 640, margin: '0 auto' }}>
      {/* Progress indicator */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 'var(--space-xl)' }}>
        {(['doe_type', 'problem', 'urgency_budget', 'details', 'confirm'] as Step[]).map((s, i) => (
          <div key={s} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: i <= ['doe_type', 'problem', 'urgency_budget', 'details', 'confirm'].indexOf(step)
              ? 'var(--color-brown)' : 'var(--color-tan)',
            transition: 'background 0.3s',
          }} />
        ))}
      </div>

      <h1 style={{ fontSize: '1.6rem', fontWeight: 700, marginBottom: 'var(--space-lg)' }}>
        {stepLabels[step]}
      </h1>

      {/* Step 1: Doe Type */}
      {step === 'doe_type' && (
        <div className="chip-group" style={{ gap: 'var(--space-md)' }}>
          {DOE_TYPES.map(doe => (
            <button
              key={doe.id}
              className={`chip ${doeType === doe.id ? 'active' : ''}`}
              onClick={() => { setDoeType(doe.id); setStep('problem'); }}
              style={{ fontSize: '1.1rem', padding: '16px 24px' }}
            >
              <span className="chip-icon">{doe.icon}</span>
              {doe.label}
            </button>
          ))}
        </div>
      )}

      {/* Step 2: Problem */}
      {step === 'problem' && PROBLEM_TAGS[doeType] && (
        <div>
          <div className="chip-group" style={{ gap: 'var(--space-md)' }}>
            {PROBLEM_TAGS[doeType].map(tag => (
              <button
                key={tag.id}
                className={`chip ${problemTag === tag.id ? 'active' : ''}`}
                onClick={() => { setProblemTag(tag.id); setStep('urgency_budget'); }}
                style={{ fontSize: '1.05rem', padding: '14px 20px' }}
              >
                {tag.label}
              </button>
            ))}
          </div>
          <button className="chip" onClick={() => setStep('doe_type')} style={{ marginTop: 'var(--space-lg)' }}>
            ← Back
          </button>
        </div>
      )}

      {/* Step 3: Urgency + Budget */}
      {step === 'urgency_budget' && (
        <div>
          <h3 style={{ marginBottom: 'var(--space-md)', fontWeight: 600 }}>How urgent?</h3>
          <div className="chip-group" style={{ marginBottom: 'var(--space-xl)' }}>
            {URGENCY_OPTIONS.map(opt => (
              <button
                key={opt.id}
                className={`chip ${urgency === opt.id ? 'active' : ''}`}
                onClick={() => setUrgency(opt.id)}
                style={urgency === opt.id ? { borderColor: opt.color, background: opt.color, color: 'white' } : {}}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <h3 style={{ marginBottom: 'var(--space-md)', fontWeight: 600 }}>Budget range?</h3>
          <div className="chip-group" style={{ marginBottom: 'var(--space-xl)' }}>
            {BUDGET_OPTIONS.map(opt => (
              <button
                key={opt.id}
                className={`chip ${budget === opt.id ? 'active' : ''}`}
                onClick={() => setBudget(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {urgency && budget && (
            <button className="btn-primary" onClick={() => setStep('details')}>
              Continue →
            </button>
          )}
          <button className="chip" onClick={() => setStep('problem')} style={{ marginLeft: 'var(--space-md)' }}>
            ← Back
          </button>
        </div>
      )}

      {/* Step 4: Optional details */}
      {step === 'details' && (
        <div>
          <div style={{ marginBottom: 'var(--space-lg)' }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 'var(--space-xs)' }}>
              Zip Code <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            <input
              type="text"
              value={zipCode}
              onChange={e => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
              placeholder="e.g. 22030"
              maxLength={5}
              style={{
                width: '100%', padding: '12px 16px', borderRadius: 'var(--radius-md)',
                border: '2px solid var(--color-tan)', fontFamily: 'var(--font-family)',
                fontSize: '1rem', background: 'var(--color-cream)',
              }}
            />
          </div>

          <div style={{ marginBottom: 'var(--space-lg)' }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 'var(--space-xs)' }}>
              Describe the issue (optional)
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="e.g. Kitchen sink is leaking under the cabinet..."
              rows={3}
              style={{
                width: '100%', padding: '12px 16px', borderRadius: 'var(--radius-md)',
                border: '2px solid var(--color-tan)', fontFamily: 'var(--font-family)',
                fontSize: '1rem', resize: 'vertical', background: 'var(--color-cream)',
              }}
            />
          </div>

          <div style={{ marginBottom: 'var(--space-lg)' }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 'var(--space-xs)' }}>
              Phone number (optional — for contractor to call)
            </label>
            <input
              type="tel"
              value={contactPhone}
              onChange={e => setContactPhone(e.target.value)}
              placeholder="(555) 123-4567"
              style={{
                width: '100%', padding: '12px 16px', borderRadius: 'var(--radius-md)',
                border: '2px solid var(--color-tan)', fontFamily: 'var(--font-family)',
                fontSize: '1rem', background: 'var(--color-cream)',
              }}
            />
          </div>

          <button className="btn-primary" onClick={() => setStep('confirm')}>
            Review & Send →
          </button>
          <button className="chip" onClick={() => setStep('urgency_budget')} style={{ marginLeft: 'var(--space-md)' }}>
            ← Back
          </button>
        </div>
      )}

      {/* Step 5: Confirm */}
      {step === 'confirm' && (
        <div>
          <div className="card" style={{ marginBottom: 'var(--space-xl)' }}>
            <h3 style={{ fontWeight: 700, marginBottom: 'var(--space-md)' }}>Work Order Summary</h3>
            <div style={{ display: 'grid', gap: 'var(--space-sm)' }}>
              <div><strong>Doe Type:</strong> {DOE_TYPES.find(d => d.id === doeType)?.icon} {DOE_TYPES.find(d => d.id === doeType)?.label}</div>
              <div><strong>Problem:</strong> {PROBLEM_TAGS[doeType]?.find(t => t.id === problemTag)?.label || 'Not specified'}</div>
              <div><strong>Urgency:</strong> <span className={`badge urgency-${urgency.replace('_', '-')}`}>{URGENCY_OPTIONS.find(u => u.id === urgency)?.label}</span></div>
              <div><strong>Budget:</strong> {BUDGET_OPTIONS.find(b => b.id === budget)?.label}</div>
              <div><strong>Zip Code:</strong> {zipCode}</div>
              {description && <div><strong>Details:</strong> {description}</div>}
              {contactPhone && <div><strong>Phone:</strong> {contactPhone}</div>}
            </div>
          </div>

          <div className="disclaimer" style={{ marginBottom: 'var(--space-lg)' }}>
            By sending this request, you agree to WorkDoe's <a href="/consumer-agreement">Consumer Agreement</a> and acknowledge that WorkDoe is a matching platform only.
          </div>

          <button
            className="btn-send-a-doe"
            onClick={handleSubmit}
            disabled={submitting}
            style={{ width: 200, height: 200, margin: '0 auto', display: 'flex' }}
          >
            <span className="doe-icon">🦌</span>
            <span className="doe-label">{submitting ? 'Sending...' : 'Confirm & Send'}</span>
          </button>

          <div style={{ textAlign: 'center', marginTop: 'var(--space-lg)' }}>
            <button className="chip" onClick={() => setStep('details')}>← Edit Details</button>
          </div>
        </div>
      )}
    </div>
  );
}
